import { ReactElement } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { enMessages } from "../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { useWindowSize } from "usehooks-ts";
import { useToast } from "@chakra-ui/react";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";
import { useQuery, useQueryClient } from "react-query";
import templatesApiService from "../Utilities/TemplatesApiService";
import AssetTemplateListItemDto from "../../../Models/Assets/AssetTemplateListItemDto";
import AssetTemplateTable from "../Components/Tables/AssetTemplateTable";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../Shared/Components/Cards/ErrorCard";

interface Props {
  isOpenDialog: { templates?: boolean };
  setIsOpenDialog: (value: any) => void;
}

export default function AssetTemplatesPage(props: Props): ReactElement {
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const { height } = useWindowSize();
  const toast = useToast({
    position: "bottom",
    duration: 5000,
    isClosable: true,
  });

  const assetTemplatesQuery = useQuery<AssetTemplateListItemDto[]>(
    [ReactQueryKeys.AllTemplatesQuery],
    () => templatesApiService.getTemplatesAssets()
  );

  const handleTemplateSaved = () => {
    assetTemplatesQuery.refetch();
    // Invalidate asset queries so Asset dialog shows updated template (e.g. pin positions)
    queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.SelectedAssetQuery] });
    toast({
      title: localizationService.toLanguageString("custom.success", enMessages.custom.success),
      description: localizationService.toLanguageString("custom.assetTemplateSaved", enMessages.custom.assetTemplateSaved),
      status: "success",
    });
  };

  const handleTemplateDeleted = () => {
    assetTemplatesQuery.refetch();
    toast({
      title: localizationService.toLanguageString("custom.success", enMessages.custom.success),
      description: localizationService.toLanguageString("custom.assetTemplateDeleted", enMessages.custom.assetTemplateDeleted),
      status: "success",
    });
  };

  const data = assetTemplatesQuery.data || [];

  if (assetTemplatesQuery.isLoading || assetTemplatesQuery.isFetching) {
    return <LoadingSpinner />;
  }

  if (assetTemplatesQuery.error) {
    return <ErrorCard />;
  }

  return (
    <div>
      <div className="mt-4">
        <AssetTemplateTable
          height={690}
          data={data}
          showTemplateSourceChoice={true}
          onTemplateSaved={handleTemplateSaved}
          onTemplateDeleted={handleTemplateDeleted}
        />
      </div>

      {props.isOpenDialog?.templates && (
        <Dialog
          title={localizationService.toLanguageString("custom.templates", enMessages.custom.templates)}
          onClose={() => props.setIsOpenDialog((prev: any) => (typeof prev === "object" && prev ? { ...prev, templates: false } : { templates: false }))}
          width="90vw"
          height={height - 100}
        >
          <AssetTemplateTable
            height={height - 200}
            data={data}
            showTemplateSourceChoice={true}
            onTemplateSaved={handleTemplateSaved}
            onTemplateDeleted={handleTemplateDeleted}
          />
        </Dialog>
      )}
    </div>
  );
}
